import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import { dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import {
  DragLocationHistory,
  DropTargetRecord,
} from "@atlaskit/pragmatic-drag-and-drop/types";
import React, { useEffect, useRef, useState } from "react";
import invariant from "tiny-invariant";
import {
  CardMoveAction,
  isCard,
  isReflectionCard,
} from "@/components/molecules/dragndrop/dragndrop";
import { cn } from "@/lib/utils";

interface ColumnCardContainerProps {
  columnId: string;
  onReflectionCardDropped?: (action: { id: string }) => void;
  onCardDropped?: (action: CardMoveAction) => void;
  children?: React.ReactNode;
}

export const ColumnCards: React.FC<ColumnCardContainerProps> = ({
  children,
  columnId,
  onReflectionCardDropped,
  onCardDropped,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isDraggedOver, setIsDraggedOver] = useState<boolean>(false);

  const handleDraggedOverChange = (
    location: DragLocationHistory,
    self: DropTargetRecord,
  ) => {
    if (location.current.dropTargets[0]?.element !== self.element) {
      setIsDraggedOver(false);
    } else {
      setIsDraggedOver(true);
    }
  };

  useEffect(() => {
    const element = ref.current;

    invariant(element);

    return combine(
      dropTargetForElements({
        element: element,
        canDrop: ({ source }) => {
          const data = source.data;
          if (onReflectionCardDropped && isReflectionCard(data)) {
            return true;
          }

          if (!isCard(data)) {
            return false;
          }

          const isSameColumn = data.columnId === columnId;
          const isFromGroup = data.parentCardId !== null;
          const isGroupParent = data.parentCardId === data.cardId;

          if (isFromGroup && !isGroupParent) {
            return true;
          }

          return !isSameColumn && !isGroupParent;
        },
        onDropTargetChange: ({ location, self }) => {
          handleDraggedOverChange(location, self);
        },
        onDragStart: ({ location, self }) => {
          handleDraggedOverChange(location, self);
        },
        onDrop: ({ source, location, self }) => {
          const data = source.data;
          if (isReflectionCard(data)) {
            onReflectionCardDropped?.({
              id: data.reflectionCardId,
            });
            setIsDraggedOver(false);
          }

          if (!isCard(data)) {
            return;
          }

          // do nothing if nested drop target is not the container itself
          if (location.current.dropTargets[0]?.element !== self.element) {
            setIsDraggedOver(false);
            return;
          }

          onCardDropped?.({
            targetType: "column",
            cardId: data.cardId,
            targetId: columnId,
          });
          setIsDraggedOver(false);
        },
      }),
    );
  }, [onReflectionCardDropped]);

  return (
    <div
      ref={ref}
      className={cn(
        "flex flex-col gap-2 pb-[90px] rounded-2xl min-h-[300px] h-full",
        isDraggedOver && "ring-2 ring-primary",
      )}
    >
      {children}
    </div>
  );
};
